/**
 * TaxHealthScoreCard — zeigt 0–10 Sterne-Score mit Item-Liste.
 *
 * Sprint 1 (Feb 2026). Wiederverwendbar in:
 *   - FL `/einstellungen` (Pflicht-Anzeige, motiviert Vervollständigung)
 *   - OnboardingWizard letzter Step (Erfolgserlebnis)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Circle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCORE_COLORS = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  bad: 'bg-rose-500',
};

function getColor(score, max) {
  const ratio = score / max;
  if (ratio >= 0.9) return SCORE_COLORS.ok;
  if (ratio >= 0.6) return SCORE_COLORS.warn;
  return SCORE_COLORS.bad;
}

export function TaxHealthScoreCard({ compact = false, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem('auth_token') || localStorage.getItem('access_token') || localStorage.getItem('token');
    if (!t) { setLoading(false); return; }
    setLoading(true);
    axios
      .get(`${API}/me/tax-health-score`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return null;
  if (!data) return null;
  const { score, max, items, is_complete } = data;
  const color = getColor(score, max);
  const ratio = (score / max) * 100;

  if (compact) {
    return (
      <div className="flex items-center gap-2" data-testid="tax-health-score-compact">
        <div className="text-xs text-slate-500">Steuer-Vorbereitung</div>
        <div className="flex items-baseline gap-1">
          <span className="text-base font-mono font-semibold text-slate-900" data-testid="tax-health-score-value">{score}</span>
          <span className="text-xs text-slate-400">/ {max}</span>
        </div>
        <div className="w-24 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
          <div className={`${color} h-full transition-all`} style={{ width: `${ratio}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="tax-health-score-card">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900">Steuer-Vorbereitung</h3>
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-mono font-bold text-slate-900" data-testid="tax-health-score-value">{score}</span>
          <span className="text-sm text-slate-400">/ {max}</span>
        </div>
      </div>
      <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden mb-4">
        <div className={`${color} h-full transition-all`} style={{ width: `${ratio}%` }} />
      </div>
      {is_complete && (
        <div className="text-xs text-emerald-700 mb-3 font-medium">
          🎉 Alle Stammdaten vollständig — dein Steuerberater hat alles, was er braucht.
        </div>
      )}
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.key} className="flex items-start gap-2 text-xs" data-testid={`health-item-${it.key}`}>
            {it.achieved ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
            ) : (
              <Circle className="w-3.5 h-3.5 text-zinc-300 shrink-0 mt-0.5" />
            )}
            <span className={it.achieved ? 'text-slate-600' : 'text-slate-500'}>
              {it.label}
              {it.hint && <span className="ml-1.5 text-slate-400">— {it.hint}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default TaxHealthScoreCard;
